import {
  GrayStyledAutocomplete,
  PageLayout,
  SuspenseWithSentryErrorBoundary,
  TabContent,
} from '@retail/components';
import { Tabs } from '@shared/components';
import { mgPrisRoutes } from '@retail/mgpris/config';
import { Outlet, useMatch, useNavigate, useParams } from 'react-router';
import { useCallback, useEffect, useMemo } from 'react';
import { useAssortmentTFunction } from '@retail/monitoring-assortment/i18n';
import { Stack } from '@mui/material';
import { MonitoringAssortment } from '@retail/monitoring-assortment/types';
import { Add } from '@mui/icons-material';
import { useDisclosure } from '@shared/hooks';
import { CreateMonitoringAssortment } from '@retail/monitoring-assortment/containers';
import { useMonitoringAssortments } from '@retail/monitoring-assortment/context';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const MarketInsightTabs = ['marketinsight', 'competitors'] as const;
type MarketInsightTab = (typeof MarketInsightTabs)[number];

export function MarketInsightPage() {
  const navigate = useNavigate();
  const t = useAssortmentTFunction();
  const marketInsightRoute = mgPrisRoutes.marketInsight;

  const { monitoringAssortments, isFetching } = useMonitoringAssortments();

  const { assortmentId } = useParams<{ assortmentId: string }>();

  const { isOpen: createAssortmentIsOpen, onToggle: toggleCreateAssortment } = useDisclosure();

  const selectedAssortment = useMemo(
    () => monitoringAssortments.find(({ id }) => id === Number(assortmentId)),
    [assortmentId, monitoringAssortments]
  );

  const selectAssortment = useCallback(
    (assortmentId?: number) => {
      if (assortmentId) {
        navigate(
          mgPrisRoutes.marketInsight.assortments.assortment.getFullLinkPath({
            assortmentId: assortmentId,
          })
        );
      } else {
        toggleCreateAssortment();
      }
    },
    [navigate, toggleCreateAssortment]
  );

  const competitorsRouteMatch = useMatch(`${marketInsightRoute.competitors.fullRoutePath}`);
  const marketInsightRouteMatch = useMatch(`${marketInsightRoute.fullRoutePath}`);

  const currentTab: MarketInsightTab = useMemo(() => {
    if (competitorsRouteMatch) return 'competitors';
    return 'marketinsight';
  }, [competitorsRouteMatch]);

  useEffect(() => {
    if (marketInsightRouteMatch && monitoringAssortments[0]) {
      navigate(
        marketInsightRoute.assortments.assortment.getFullLinkPath({
          assortmentId: monitoringAssortments[0].id,
        })
      );
    }
  }, [
    marketInsightRoute.assortments.assortment,
    marketInsightRouteMatch,
    monitoringAssortments,
    navigate,
  ]);

  useEffect(() => {
    if (marketInsightRouteMatch && !monitoringAssortments.length) {
      navigate(marketInsightRoute.assortments.getFullLinkPath());
    }
  }, [marketInsightRoute.assortments, marketInsightRouteMatch, monitoringAssortments, navigate]);

  return (
    <PageLayout maxWidth="full" fixedHeight>
      <Stack flexDirection="row" justifyContent="space-between" alignItems="flex-end">
        <Tabs<MarketInsightTab>
          prefixId="market-insight"
          tabs={[
            {
              label: t('assortment.marketInsight'),
              value: 'marketinsight',
              to: marketInsightRoute.getFullLinkPath(),
            },
            {
              label: t('assortment.competitors.heading'),
              value: 'competitors',
              to: marketInsightRoute.competitors.getFullLinkPath(),
            },
          ]}
          currentTab={currentTab}
        />

        {!competitorsRouteMatch && (
          <GrayStyledAutocomplete
            options={monitoringAssortments}
            selectedOption={selectedAssortment}
            searchFieldPlaceholder={t('assortment.admin.search.assortmentSearch')}
            nonListOption={
              <Stack component="span" flexDirection="row" alignItems="center" gap={3}>
                <Add fontSize="small" />
                {t('assortment.admin.create.title')}
              </Stack>
            }
            noOptionsText={
              monitoringAssortments.length === 0 ? t('assortment.noAssortmentExists') : undefined
            }
            isLoading={isFetching}
            onSelectOption={selectAssortment}
            getOptionLabel={(assortment?: MonitoringAssortment) => assortment?.title}
            getOptionId={(assortment: MonitoringAssortment) => assortment.id}
          />
        )}
      </Stack>

      <TabContent sx={{ overflow: 'hidden', p: 0, borderTopRightRadius: 0 }}>
        <SuspenseWithSentryErrorBoundary>
          <Outlet context={{ monitoringAssortments, selectedAssortment }} />
        </SuspenseWithSentryErrorBoundary>
      </TabContent>

      <CreateMonitoringAssortment
        openCreateManuallyModal={createAssortmentIsOpen}
        toggleCreateManuallyModal={toggleCreateAssortment}
      />
    </PageLayout>
  );
}
